import { useEffect, useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { useAtom } from 'jotai';

import { isWebImageType } from '@/modules/annotator/annotation-utils/annotation-utils';
import { useLatestImagesV2 } from '@/react-query/useLatestImagesv2';
import { usePreferences } from '@/react-query/usePreferences';

import {
  type AdjustmentVersion,
  useGetSharpenedImage,
} from '../../../../react-query/useGetSharpenedImages';

import {
  finalFilterAtom,
  leftFilterAtom,
  selectedAdjustmentTypeAtom,
  selectedImageIdAtom,
  selectedPatientIdAtom,
  selectedVisitAtom,
} from './atoms';
import { DisplayImageForComparison } from './DisplayImageForComparison';
import {
  type DefaultFilterInfo,
  adjustmentVersionLabel,
  getDefaultFilterInfo,
  getFilterOptions,
} from './helpers';

interface FilterSelectContentProps {
  availableSharpeningFilters?: AdjustmentVersion[];
  filter: AdjustmentVersion | null;
  onFilterChange: (filter: AdjustmentVersion | null) => void;
  defaultFilterInfo: DefaultFilterInfo;
}

const FilterSelect = ({
  availableSharpeningFilters,
  filter,
  onFilterChange,
  defaultFilterInfo,
}: FilterSelectContentProps) => {
  const { currentFilter, allFilters, showMultipleHdLabels } = defaultFilterInfo;
  const filterOptions = getFilterOptions(
    availableSharpeningFilters ?? [],
    defaultFilterInfo,
  );

  const defaultBadges = (f: AdjustmentVersion | null) => {
    if (f === null) return null;
    if (showMultipleHdLabels) {
      const matchingSlots = allFilters.filter(
        (slot) => slot.value !== null && slot.value === f,
      );
      if (matchingSlots.length === 0) return null;
      return (
        <span className="vai-flex vai-gap-1 vai-text-xs vai-text-gray-500 vai-font-medium">
          {matchingSlots.map((slot, i) => {
            const slotLabel = slot.label;
            return (
              <span key={slot.label}>
                {i > 0 && <span className="vai-opacity-70"> · </span>}
                <Trans>(Current {slotLabel} Default)</Trans>
              </span>
            );
          })}
        </span>
      );
    }
    return f === currentFilter ? (
      <span className="vai-text-xs vai-text-gray-500 vai-font-medium">
        <Trans>(Current Default Filter)</Trans>
      </span>
    ) : null;
  };

  return (
    <Select
      value={filter || 'original'}
      onValueChange={(v: AdjustmentVersion | 'original') =>
        onFilterChange(v === 'original' ? null : v)
      }
    >
      <SelectTrigger>
        <SelectValue>
          <div className="vai-flex vai-flex-row vai-flex-wrap vai-items-center vai-gap-2">
            {filter ? (
              <span>
                {adjustmentVersionLabel(filter).label} ({filter})
              </span>
            ) : (
              <Trans>Original: No Enhancement</Trans>
            )}
            {defaultBadges(filter)}
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent>
        {filterOptions.map((filterOption) => (
          <SelectItem
            key={filterOption || 'original'}
            value={filterOption || 'original'}
          >
            <div className="vai-flex vai-flex-col vai-gap-1">
              <div className="vai-flex vai-items-center vai-gap-2 vai-flex-wrap">
                {filterOption ? (
                  <span>
                    {`${adjustmentVersionLabel(filterOption).label} (${filterOption})`}
                  </span>
                ) : (
                  <span>
                    <Trans>Original: No Enhancement</Trans>
                  </span>
                )}
                {defaultBadges(filterOption)}
              </div>
              <span className="vai-text-sm vai-text-gray-500">
                {adjustmentVersionLabel(filterOption).description}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const FilterComparisonPreview = ({}) => {
  const [selectedVisit] = useAtom(selectedVisitAtom);
  const [selectedPatientId] = useAtom(selectedPatientIdAtom);
  const [selectedImageId] = useAtom(selectedImageIdAtom);
  const [leftFilter, setLeftFilter] = useAtom(leftFilterAtom);
  const [rightFilter, setRightFilter] = useAtom(finalFilterAtom);
  const [selectedAdjustmentType] = useAtom(selectedAdjustmentTypeAtom);

  const visitId = selectedVisit ?? 0;
  const patientId = selectedPatientId ?? '';
  const imageId = selectedImageId;
  const leftImageQuery = useGetSharpenedImage({
    imageId: imageId || null,
    adjustmentVersion: leftFilter,
  });

  const rightImageQuery = useGetSharpenedImage({
    imageId: imageId || null,
    adjustmentVersion: rightFilter,
  });

  const {
    data: images,
    isLoading: isLoadingImages,
    isError: isErrorImages,
  } = useLatestImagesV2({
    videaPatientId: patientId,
    estimatedVisitId: visitId.toString(),
  });

  const { data: preferences } = usePreferences();
  const defaultFilterInfo = getDefaultFilterInfo(
    preferences,
    selectedAdjustmentType,
  );
  const currentDefaultFilter = defaultFilterInfo.currentFilter;
  const availableSharpeningFilters = preferences?.availableSharpeningFilters;

  useEffect(() => {
    setLeftFilter(currentDefaultFilter);
    setRightFilter(currentDefaultFilter);
  }, [
    selectedAdjustmentType,
    currentDefaultFilter,
    setLeftFilter,
    setRightFilter,
  ]);

  const originalImage = useMemo(() => {
    const originalImageInfo = images?.analyses?.find(
      (image) => image.imageId === Number(imageId),
    );
    return isWebImageType(originalImageInfo?.path)
      ? originalImageInfo?.path
      : originalImageInfo?.originalPath;
  }, [images, imageId]);

  const selectedHDVersion = selectedAdjustmentType;

  return (
    <div className="vai-flex vai-flex-col vai-gap-3">
      <div className="vai-grid vai-grid-cols-2 vai-gap-4">
        {/* Left Side - Comparison */}
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className=" vai-h-7 vai-flex vai-items-center vai-justify-between vai-gap-2 vai-text-sm">
            <Trans>Compare With</Trans>
          </div>
          <FilterSelect
            filter={leftFilter}
            onFilterChange={setLeftFilter}
            availableSharpeningFilters={availableSharpeningFilters}
            defaultFilterInfo={defaultFilterInfo}
          />
          <div className="vai-relative vai-aspect-video vai-bg-gray-200 vai-rounded vai-overflow-hidden">
            <DisplayImageForComparison
              {...(leftFilter === null
                ? {
                    image: originalImage,
                    loading: isLoadingImages,
                    error: isErrorImages,
                  }
                : {
                    image: leftImageQuery.data?.s3Path,
                    loading: leftImageQuery.isLoading,
                    error: leftImageQuery.isError,
                  })}
              label={adjustmentVersionLabel(leftFilter).label}
              isLeftFilter={true}
            />
          </div>
        </div>

        {/* Right Side - Your Selection */}
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-items-center vai-justify-between vai-gap-2 vai-text-sm vai-h-7">
            <span>
              <Trans>Your Selection</Trans>
            </span>
            <span className="vai-bg-blue-600 vai-text-white vai-px-2 vai-py-1 vai-rounded-full">
              <Trans>Will Save as </Trans>
              {defaultFilterInfo.showMultipleHdLabels
                ? selectedHDVersion
                : 'HD'}
            </span>
          </div>
          <FilterSelect
            filter={rightFilter}
            onFilterChange={setRightFilter}
            availableSharpeningFilters={availableSharpeningFilters}
            defaultFilterInfo={defaultFilterInfo}
          />
          <div className="vai-relative vai-aspect-video vai-rounded vai-overflow-hidden vai-border-2 vai-border-blue-600">
            <DisplayImageForComparison
              {...(rightFilter === null
                ? {
                    image: originalImage,
                    loading: isLoadingImages,
                    error: isErrorImages,
                  }
                : {
                    image: rightImageQuery.data?.s3Path,
                    loading: rightImageQuery.isLoading,
                    error: rightImageQuery.isError,
                  })}
              label={adjustmentVersionLabel(rightFilter).label}
              isLeftFilter={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
